import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Select,
  MenuItem,
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormGroup,
  Autocomplete,
  Switch,
  Tooltip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";

import KeywordBuilder from "./KeywordBuilder";
import SubtestRangeRecommendButton from "./SubtestRangeRecommendButton";
import SubtestLabHistoryButton from "./SubtestLabHistoryButton";
import NormalKeywordRecommendButton from "./NormalKeywordRecommendButton";

import {
  fetchCategorySubtests,
  setOption,
  updateSubtest,
  deleteSubtest,
  splitSubtest,
  saveNormalRuleset,
} from "../reducers/ruleFrameSlice";

import BloodTestsManager from "./BloodTestManager";

const KEYWORD_RULE_DEFS = [
  {
    key: "keywordPresentReport",
    label: "Keyword Present — Report",
    checkType: "Present",
    source: "Report",
  },
  {
    key: "keywordAbsentReport",
    label: "Keyword Absent — Report",
    checkType: "Absent",
    source: "Report",
  },
  {
    key: "keywordPresentProblemPage",
    label: "Keyword Present — Problem Page",
    checkType: "Present",
    source: "Problem Page",
  },
  {
    key: "keywordAbsentProblemPage",
    label: "Keyword Absent — Problem Page",
    checkType: "Absent",
    source: "Problem Page",
  },
];

const MEDICATION_RULE_DEFS = [
  {
    key: "medicationPresent",
    label: "Medication Present",
    outcome: "Present",
  },
  {
    key: "medicationAbsent",
    label: "Medication Not Present",
    outcome: "Not Present",
  },
];

const EMPTY_KEYWORD_RULE = { enabled: false, keywords: "" };
const EMPTY_MEDICATION_RULE = {
  enabled: false,
  issuedInMonths: "",
  medications: "",
};

function safeParse(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function parseWithFallback(raw, fallback) {
  const parsed = safeParse(raw);
  return parsed ? { ...fallback, ...parsed } : { ...fallback };
}

function formatKeywordsHuman(serialized) {
  if (!serialized || !serialized.trim()) return "No keywords configured";

  const groups = serialized
    .split(",")
    .map((g) =>
      g
        .split("+")
        .map((k) => k.trim())
        .filter(Boolean),
    )
    .filter((g) => g.length > 0);

  if (groups.length === 0) return "No keywords configured";

  return groups
    .map((g) => (g.length > 1 ? `(${g.join(" OR ")})` : g[0]))
    .join(" AND ");
}

function formatMedicationHuman(rule) {
  if (!rule || !rule.medications || !rule.medications?.trim()) {
    return "No medications configured";
  }

  const kwText = formatKeywordsHuman(rule.medications);

  const monthsText = rule.issuedInMonths
    ? `issued within last ${rule.issuedInMonths} month${
        String(rule.issuedInMonths) === "1" ? "" : "s"
      }`
    : "no time limit set";

  return `${kwText} — ${monthsText}`;
}

function MedicationBuilder({
  value = {},
  onChange,
  disabled = false,
  hideAddRequiredGroup = false,
}) {
  const medications =
    typeof value === "string"
      ? value
      : Array.isArray(value?.medications)
        ? value.medications.join("+")
        : String(value?.medications ?? "");

  const issuedInMonths =
    typeof value === "object" && !Array.isArray(value)
      ? value?.issuedInMonths || ""
      : "";

  const handleMonthsChange = (newValue) => {
    onChange({
      issuedInMonths: newValue,
      medications,
    });
  };

  const handleMedicationChange = (newValue) => {
    onChange({
      issuedInMonths,
      medications: newValue,
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <TextField
        size="small"
        label="Medication issued in last (Months)"
        type="text"
        value={issuedInMonths}
        onChange={(e) => {
          const val = e.target.value;

          if (/^[1-9]\d*$/.test(val) || val === "") {
            handleMonthsChange(val);
          }
        }}
        inputProps={{
          inputMode: "numeric",
          pattern: "[1-9][0-9]*",
        }}
        disabled={disabled}
        sx={{
          width: 260,
          "& .MuiInputBase-input": {
            fontSize: "0.8rem",
            padding: "6px 8px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.75rem",
          },
        }}
      />

      <KeywordBuilder
        value={medications}
        onChange={handleMedicationChange}
        disabled={disabled}
        title="Medications to Check"
        hideAddRequiredGroup={hideAddRequiredGroup}
      />
    </Box>
  );
}

function RuleRow({ label, enabled, summary, onToggle, onEdit, onDelete }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.4,
        px: 0.5,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Tooltip
        title={
          enabled ? "Enabled — click to disable" : "Disabled — click to enable"
        }
      >
        <Switch
          size="small"
          checked={!!enabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </Tooltip>

      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 600,
          minWidth: 190,
          color: enabled ? "text.primary" : "text.disabled",
        }}
      >
        {label}
      </Typography>

      <Typography
        noWrap
        title={summary}
        sx={{
          fontSize: "0.72rem",
          color: enabled ? "text.secondary" : "#bbb",
          fontStyle: enabled ? "normal" : "italic",
          flex: 1,
          minWidth: 0,
        }}
      >
        {enabled ? summary : "Disabled"}
      </Typography>

      <Tooltip title="Configure">
        <IconButton size="small" onClick={onEdit}>
          <EditIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Clear this rule">
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function KeywordCheckDialog({
  open,
  title,
  checkType,
  source,
  hideAddRequiredGroup,
  value,
  onChange,
  onSave,
  onCancel,
  licenseKey,
  category,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle sx={{ fontSize: "1rem" }}>{title}</DialogTitle>

      <DialogContent dividers>
        <Typography
          sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1.5 }}
        >
          Checking for keyword <strong>{checkType}</strong> in the{" "}
          <strong>{source}</strong>.
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <NormalKeywordRecommendButton
            licenseKey={licenseKey}
            category={category}
            checkType={checkType}
            source={source}
            onApply={(rec) => {
              if (rec?.keywords !== undefined) {
                onChange({ ...value, keywords: rec.keywords });
              }
            }}
          />
        </Box>

        <KeywordBuilder
          value={value.keywords}
          onChange={(serialized) =>
            onChange({ ...value, keywords: serialized })
          }
          title="Keywords to Check"
          hideAddRequiredGroup={hideAddRequiredGroup}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MedicationCheckDialog({
  open,
  title,
  hideAddRequiredGroup,
  value,
  onChange,
  onSave,
  onCancel,
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle sx={{ fontSize: "1rem" }}>{title}</DialogTitle>

      <DialogContent dividers>
        <MedicationBuilder
          value={value}
          onChange={(newVal) => onChange({ ...value, ...newVal })}
          hideAddRequiredGroup={hideAddRequiredGroup}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AccurxMessageDialog({ open, value, onChange, onSave, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>
        Accurx Message for Normal Report
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter the message to send via Accurx when a normal report is filed…"
          sx={{ "& .MuiInputBase-input": { fontSize: "0.85rem" } }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>
          Save Message
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BloodTestsTable({ subTests, deploymentType, license_key }) {
  const dispatch = useDispatch();
  const { selectedCategory } = useSelector((state) => state.ruleFrame);
  const handleDelete = (id) => {
    dispatch(deleteSubtest(id));
  };
  const handleSplit = (id) => {
    dispatch(splitSubtest(id));
  };
  const handleEdit = (id, currentName) => {
    const newName = prompt("Enter new subtest name:", currentName);

    if (newName && newName.trim()) {
      dispatch(
        updateSubtest({
          id,
          changes: {
            sub_test_name: newName,
          },
        }),
      );
    }
  };

  const handleFieldChange = (id, field, value) => {
    dispatch(
      updateSubtest({
        id,
        changes: {
          [field]: value,
        },
      }),
    );
  };

  const handleApplySuggestions = (baseRow, selectedSuggestions) => {
    if (!selectedSuggestions?.length) return;

    const first = selectedSuggestions[0];

    dispatch(
      updateSubtest({
        id: baseRow.id,
        changes: {
          gender: first.gender || "",
          min_age: first.min_age,
          max_age: first.max_age,
          min_val: first.min_val,
          max_val: first.max_val,
        },
      }),
    );

    selectedSuggestions.slice(1).forEach((row) => {
      dispatch({
        type: "ruleFrame/addSubtest",
        payload: {
          sub_test_name: baseRow.sub_test_name,
          gender: row.gender || "",
          min_age: row.min_age,
          max_age: row.max_age,
          min_val: row.min_val,
          max_val: row.max_val,
        },
      });
    });
  };

  const columns = [
    {
      field: "sub_test_name",
      headerName: "Test Name",

      renderHeader: () => (
        <strong>Pathology Tests – {selectedCategory || "None"}</strong>
      ),

      flex: 2.5,
      minWidth: 120,

      renderCell: (params) => {
        const { sub_test_name, gender, min_age, max_age } = params.row;

        let genderLabel = "";

        if (gender === "Male") {
          genderLabel = "M";
        } else if (gender === "Female") {
          genderLabel = "F";
        }

        let ageLabel = "";

        if (min_age && max_age) {
          ageLabel = `${min_age} to ${max_age}`;
        } else if (min_age) {
          ageLabel = `${min_age}+`;
        } else if (max_age) {
          ageLabel = `0 to ${max_age}`;
        }

        let suffix = "";

        if (genderLabel || ageLabel) {
          if (genderLabel && ageLabel) {
            suffix = ` (${genderLabel} - ${ageLabel})`;
          } else if (genderLabel) {
            suffix = ` (${genderLabel})`;
          } else if (ageLabel) {
            suffix = ` (${ageLabel})`;
          }
        }

        return `${sub_test_name}${suffix}`;
      },
    },

    {
      field: "gender",
      headerName: "Gender",
      flex: 0.6,

      renderCell: (params) => (
        <Select
          value={params.row.gender ?? ""}
          onChange={(e) =>
            handleFieldChange(params.row.id, "gender", e.target.value)
          }
          sx={{
            width: "100%",
            "& .MuiSelect-select": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem value="" sx={{ fontSize: "0.75rem", height: "26px" }}>
            None
          </MenuItem>

          <MenuItem value="Male" sx={{ fontSize: "0.75rem", height: "26px" }}>
            Male
          </MenuItem>

          <MenuItem value="Female" sx={{ fontSize: "0.75rem", height: "26px" }}>
            Female
          </MenuItem>
        </Select>
      ),
    },

    {
      field: "min_age",
      headerName: "Min Age",
      flex: 0.6,

      renderCell: (params) => (
        <TextField
          defaultValue={params.row.min_age ?? ""}
          type="number"
          inputProps={{ min: 0, step: 1 }}
          onBlur={(e) => {
            const value = e.target.value.trim();

            if (value === "") {
              handleFieldChange(params.row.id, "min_age", "");
              return;
            }

            const num = Number(value);

            if (Number.isInteger(num) && num > 0) {
              handleFieldChange(params.row.id, "min_age", e.target.value);
            } else {
              e.target.value = params.row.max_age ?? "";
            }
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },

    {
      field: "max_age",
      headerName: "Max Age",
      flex: 0.6,

      renderCell: (params) => (
        <TextField
          defaultValue={params.row.max_age ?? ""}
          type="number"
          inputProps={{ min: 1, step: 1 }}
          onBlur={(e) => {
            const value = e.target.value.trim();

            if (value === "") {
              handleFieldChange(params.row.id, "max_age", "");
              return;
            }

            const num = Number(value);

            if (Number.isInteger(num) && num > 0) {
              handleFieldChange(params.row.id, "max_age", e.target.value);
            } else {
              e.target.value = params.row.max_age ?? "";
            }
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },

    { field: "lab_min_val", headerName: "Lab Min", flex: 0.6 },
    { field: "lab_max_val", headerName: "Lab Max", flex: 0.6 },

    {
      field: "min_val",
      headerName: "Clin Min",
      flex: 0.6,

      renderCell: (params) => (
        <TextField
          value={params.row.min_val ?? ""}
          type="text"
          inputProps={{ min: 0, step: "any" }}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "") {
              handleFieldChange(params.row.id, "min_val", "");
              return;
            }

            if (/^\d*\.?\d*$/.test(value)) {
              handleFieldChange(params.row.id, "min_val", value);
            }
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },

    {
      field: "max_val",
      headerName: "Clin Max",
      flex: 0.6,

      renderCell: (params) => (
        <TextField
          value={params.row.max_val ?? ""}
          type="text"
          inputProps={{ min: 0, step: "any" }}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "") {
              handleFieldChange(params.row.id, "max_val", "");
              return;
            }

            if (/^\d*\.?\d*$/.test(value)) {
              handleFieldChange(params.row.id, "max_val", value);
            }
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
            },
          }}
        />
      ),
    },

    {
      field: "previous_type",
      headerName: "Prev Result",
      flex: 1,

      renderCell: (params) => (
        <Select
          defaultValue={params.row.previous_type ?? 0}
          onChange={(e) =>
            handleFieldChange(
              params.row.id,
              "previous_type",
              parseInt(e.target.value, 10),
            )
          }
          sx={{
            width: "100%",
            "& .MuiSelect-select": {
              fontSize: "0.75rem",
              padding: "2px 6px",
              height: "20px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem
            value={0}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            No Check
          </MenuItem>

          <MenuItem
            value={1}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            Prev test within X days
          </MenuItem>

          <MenuItem
            value={2}
            sx={{ width: "100%", fontSize: "0.75rem", height: "26px" }}
          >
            Prev result
          </MenuItem>
        </Select>
      ),
    },

    {
      field: "days_or_tests",
      headerName: "Days",
      flex: 0.6,

      renderCell: (params) => {
        if (params.row.previous_type === 0) return null;
        if (params.row.previous_type === 2) return null;

        return (
          <TextField
            key={`${params.row.id}-${params.row.previous_type}-${params.row.days_or_tests}`}
            defaultValue={params.row.days_or_tests ?? ""}
            type="number"
            inputProps={{ min: 1, step: 1 }}
            onBlur={(e) => {
              const value = e.target.value.trim();

              if (value === "") {
                handleFieldChange(params.row.id, "days_or_tests", "");
                return;
              }

              const num = Number(value);

              if (Number.isInteger(num) && num > 0) {
                handleFieldChange(params.row.id, "days_or_tests", num);
              } else {
                e.target.value = params.row.days_or_tests ?? "";
              }
            }}
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },

    {
      field: "pdiv_below",
      headerName: "Below %",
      flex: 0.6,

      renderCell: (params) => {
        if (params.row.previous_type === 0) return null;

        return (
          <TextField
            defaultValue={params.row.pdiv_below ?? ""}
            type="number"
            onBlur={(e) =>
              handleFieldChange(params.row.id, "pdiv_below", e.target.value)
            }
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },

    {
      field: "pdiv_above",
      headerName: "Above %",
      flex: 0.6,

      renderCell: (params) => {
        if (params.row.previous_type === 0) return null;

        return (
          <TextField
            defaultValue={params.row.pdiv_above ?? ""}
            type="number"
            onBlur={(e) =>
              handleFieldChange(params.row.id, "pdiv_above", e.target.value)
            }
            sx={{
              width: "100%",
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "2px 6px",
                height: "20px",
              },
            }}
          />
        );
      },
    },

    ...(deploymentType !== "runner"
      ? [
          {
            field: "suggest_ranges",
            headerName: "",
            flex: 0.2,
            sortable: false,

            renderCell: (params) => (
              <SubtestRangeRecommendButton
                licenseKey={license_key}
                category={selectedCategory}
                subtest={params.row}
                onApply={(selectedRows) =>
                  handleApplySuggestions(params.row, selectedRows)
                }
              />
            ),
          },

          {
            field: "lab_history",
            headerName: "",
            flex: 0.2,
            sortable: false,

            renderCell: (params) => (
              <SubtestLabHistoryButton
                licenseKey={license_key}
                category={selectedCategory}
                subtest={params.row}
                onApply={(selectedRows) =>
                  handleApplySuggestions(params.row, selectedRows)
                }
              />
            ),
          },

          {
            field: "split",
            headerName: "",
            flex: 0.2,

            renderCell: (params) => (
              <Button
                color="primary"
                variant="superdense"
                onClick={() => {
                  const bloodTestName = params.row.sub_test_name;
                  const result = window.confirm(
                    `Are you sure you want split ${bloodTestName} for both genders?`,
                  );

                  if (result) {
                    handleSplit(params.row.id);
                  } else {
                    window.alert("The action has been cancelled.");
                  }
                }}
              >
                Split
              </Button>
            ),
          },

          {
            field: "delete",
            headerName: "",
            flex: 0.2,

            renderCell: (params) => (
              <Button
                color="error"
                variant="superdense"
                onClick={() => {
                  const bloodTestName = params.row.sub_test_name;

                  const result = window.confirm(
                    `Are you sure you want to delete ${bloodTestName}?`,
                  );

                  if (result) {
                    handleDelete(params.row.id);
                  } else {
                    window.alert("The action has been cancelled.");
                  }
                }}
              >
                Delete
              </Button>
            ),
          },

          {
            field: "edit",
            headerName: "",
            flex: 0.2,

            renderCell: (params) => (
              <Button
                color="primary"
                variant="superdense"
                onClick={() =>
                  handleEdit(params.row.id, params.row.sub_test_name)
                }
              >
                Edit
              </Button>
            ),
          },

          {
            field: "deactivated",
            headerName: "",
            flex: 0.2,

            renderCell: (params) => (
              <Tooltip
                title={params.row.deactivated ? "Deactivated" : "Active"}
              >
                <Switch
                  size="small"
                  checked={!params.row.deactivated}
                  onChange={(e) =>
                    handleFieldChange(
                      params.row.id,
                      "deactivated",
                      !e.target.checked,
                    )
                  }
                  color="success"
                />
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        rows={subTests}
        columns={columns}
        variant="superdense"
        density="compact"
        hideFooterSelectedRowCount
        sx={{
          height: 570,
          "& .MuiDataGrid-columnHeaders": { marginBottom: "0px" },
          "& .MuiToolbar-root": { maxHeight: "20px", minHeight: "20px" },
          "& .MuiTablePagination-toolbar": {
            maxHeight: "20px",
            minHeight: "20px",
          },
          "& .MuiDataGrid-footerContainer": {
            height: "20px",
            minHeight: "20px",
            maxHeight: "20px",
            overflow: "hidden",

            "& .MuiTablePagination-root": {
              height: "20px",
              minHeight: "20px",
              overflow: "hidden",
            },

            "& .MuiTablePagination-toolbar": {
              minHeight: "20px",
              height: "20px",
              padding: 0,
            },
          },
        }}
        disableColumnMenu
        pageSize={20}
        rowsPerPageOptions={[20]}
        disableSelectionOnClick
        getRowId={(row) => row.id}
        initialState={{
          sorting: {
            sortModel: [{ field: "sub_test_name", sort: "asc" }],
          },
        }}
      />
    </div>
  );
}

const getSubtestUniqueKey = (st) => {
  const name = String(st.sub_test_name ?? st.name ?? "")
    .trim()
    .toLowerCase();

  let gender = String(st.gender ?? "")
    .trim()
    .toLowerCase();

  if (gender === "" || gender === "both") {
    gender = "both";
  }

  const minAge =
    st.min_age === null ||
    st.min_age === undefined ||
    String(st.min_age).trim() === ""
      ? ""
      : String(st.min_age).trim();

  const maxAge =
    st.max_age === null ||
    st.max_age === undefined ||
    String(st.max_age).trim() === ""
      ? ""
      : String(st.max_age).trim();

  return `${name}|${gender}|${minAge}|${maxAge}`;
};

const NormalRuleFrame = () => {
  const dispatch = useDispatch();

  const {
    selectedCategory,
    ruleloading,
    ruleerror,
    rulesuccess,
    subtests,
    options,
    deploymentType,
    category_data,
  } = useSelector((state) => state.ruleFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const [keywordRules, setKeywordRules] = useState(() =>
    Object.fromEntries(
      KEYWORD_RULE_DEFS.map((d) => [d.key, { ...EMPTY_KEYWORD_RULE }]),
    ),
  );

  const [medicationRules, setMedicationRules] = useState(() =>
    Object.fromEntries(
      MEDICATION_RULE_DEFS.map((d) => [d.key, { ...EMPTY_MEDICATION_RULE }]),
    ),
  );

  const [keywordDialogKey, setKeywordDialogKey] = useState(null);
  const [keywordDraft, setKeywordDraft] = useState(EMPTY_KEYWORD_RULE);

  const [medicationDialogKey, setMedicationDialogKey] = useState(null);
  const [medicationDraft, setMedicationDraft] = useState(EMPTY_MEDICATION_RULE);

  const [accurxDialogOpen, setAccurxDialogOpen] = useState(false);
  const [accurxDraft, setAccurxDraft] = useState("");

  useEffect(() => {
    setKeywordRules((prev) => {
      const next = { ...prev };
      KEYWORD_RULE_DEFS.forEach(({ key }) => {
        next[key] = parseWithFallback(options[key], EMPTY_KEYWORD_RULE);
      });
      return next;
    });
  }, [
    options.keywordPresentReport,
    options.keywordAbsentReport,
    options.keywordPresentProblemPage,
    options.keywordAbsentProblemPage,
  ]);

  useEffect(() => {
    setMedicationRules((prev) => {
      const next = { ...prev };
      MEDICATION_RULE_DEFS.forEach(({ key }) => {
        next[key] = parseWithFallback(options[key], EMPTY_MEDICATION_RULE);
      });
      return next;
    });
  }, [options.medicationPresent, options.medicationAbsent]);

  const persistKeywordRule = (key, value) => {
    setKeywordRules((prev) => ({ ...prev, [key]: value }));
    dispatch(setOption({ key, value: JSON.stringify(value) }));
  };

  const persistMedicationRule = (key, value) => {
    setMedicationRules((prev) => ({ ...prev, [key]: value }));
    dispatch(setOption({ key, value: JSON.stringify(value) }));
  };

  const handleToggleKeywordRule = (key, checked) => {
    const current = keywordRules[key] || EMPTY_KEYWORD_RULE;

    if (checked && !current.keywords?.trim()) {
      setKeywordDraft({ ...current, enabled: true });
      setKeywordDialogKey(key);
      return;
    }

    persistKeywordRule(key, { ...current, enabled: checked });
  };

  const handleDeleteKeywordRule = (key, label) => {
    if (!window.confirm(`Clear the "${label}" rule?`)) return;
    persistKeywordRule(key, { ...EMPTY_KEYWORD_RULE });
  };

  const openKeywordDialog = (key) => {
    setKeywordDraft({ ...(keywordRules[key] || EMPTY_KEYWORD_RULE) });
    setKeywordDialogKey(key);
  };

  const saveKeywordDialog = () => {
    const value = {
      ...keywordDraft,
      enabled: !!keywordDraft.keywords?.trim(),
    };
    persistKeywordRule(keywordDialogKey, value);
    setKeywordDialogKey(null);
  };

  const cancelKeywordDialog = () => setKeywordDialogKey(null);

  const handleToggleMedicationRule = (key, checked) => {
    const current = medicationRules[key] || EMPTY_MEDICATION_RULE;

    if (
      checked &&
      (!current.medications?.trim() ||
        !current.issuedInMonths?.toString().trim())
    ) {
      setMedicationDraft({ ...current, enabled: true });
      setMedicationDialogKey(key);
      return;
    }

    persistMedicationRule(key, { ...current, enabled: checked });
  };

  const handleDeleteMedicationRule = (key, label) => {
    if (!window.confirm(`Clear the "${label}" rule?`)) return;
    persistMedicationRule(key, { ...EMPTY_MEDICATION_RULE });
  };

  const openMedicationDialog = (key) => {
    setMedicationDraft({ ...(medicationRules[key] || EMPTY_MEDICATION_RULE) });
    setMedicationDialogKey(key);
  };

  const saveMedicationDialog = () => {
    const hasBoth =
      medicationDraft.medications?.trim() &&
      medicationDraft.issuedInMonths?.toString().trim();

    const value = { ...medicationDraft, enabled: !!hasBoth };
    persistMedicationRule(medicationDialogKey, value);
    setMedicationDialogKey(null);
  };

  const cancelMedicationDialog = () => setMedicationDialogKey(null);

  /* ---------------------- Accurx message dialog --------------------- */

  const openAccurxDialog = () => {
    setAccurxDraft(options.accurxNormalMessage || "");
    setAccurxDialogOpen(true);
  };

  const saveAccurxDialog = () => {
    dispatch(setOption({ key: "accurxNormalMessage", value: accurxDraft }));
    setAccurxDialogOpen(false);
  };

  const cancelAccurxDialog = () => setAccurxDialogOpen(false);

  /* --------------------------- Static data --------------------------- */

  const defaultComments = [
    "Normal No Action",
    "** Normal",
    "** Abnormal",
    "** Satisfactory",
    "*Expected/stable",
    "*On correct treatment",
  ];

  /* ----------------------------- Effects ------------------------------ */

  useEffect(() => {
    if (options.fileMethod === 1 && !options.fileComment) {
      dispatch(setOption({ key: "fileComment", value: "Normal No Action" }));
    }

    if (options.fileMethod === 0) {
      dispatch(setOption({ key: "fileComment", value: "" }));
    }
  }, [options.fileMethod, options.fileComment, dispatch]);

  useEffect(() => {
    if (selectedCategory && license_key) {
      dispatch(
        fetchCategorySubtests({
          categoryName: selectedCategory,
          license_key,
        }),
      );
    }
  }, [dispatch, license_key, selectedCategory]);

  useEffect(() => {
    if (!category_data) return;

    dispatch(
      setOption({
        key: "skipWithoutRanges",
        value: category_data.skip_without_ranges,
      }),
    );
    dispatch(
      setOption({ key: "fileNormal", value: category_data.file_normal }),
    );
    dispatch(setOption({ key: "followLab", value: category_data.follow_lab }));
    dispatch(
      setOption({
        key: "sendAccurxNormal",
        value: category_data.send_accurx_normal || false,
      }),
    );
    dispatch(
      setOption({
        key: "accurxNormalMessage",
        value: category_data.accurx_normal_message || "",
      }),
    );
    dispatch(
      setOption({
        key: "fileComment",
        value: category_data.file_comment || "",
      }),
    );
    dispatch(
      setOption({ key: "fileMethod", value: category_data.file_method || 0 }),
    );

    KEYWORD_RULE_DEFS.forEach(({ key }) => {
      let value = safeParse(category_data[key]);

      if (!value) {
        if (
          key === "keywordPresentReport" &&
          category_data.keyword_check === 1 &&
          category_data.keyword
        ) {
          value = { enabled: true, keywords: category_data.keyword };
        } else if (
          key === "keywordAbsentProblemPage" &&
          category_data.keyword_check === 3 &&
          category_data.keyword
        ) {
          value = { enabled: true, keywords: category_data.keyword };
        }
      }

      dispatch(
        setOption({
          key,
          value: JSON.stringify(value || { ...EMPTY_KEYWORD_RULE }),
        }),
      );
    });

    MEDICATION_RULE_DEFS.forEach(({ key }) => {
      const legacy = safeParse(category_data[key]);

      const value = {
        issuedInMonths: legacy?.issuedInMonths || "",
        medications: legacy?.medications || "",
        enabled:
          legacy?.enabled ?? !!(legacy?.issuedInMonths && legacy?.medications),
      };

      dispatch(setOption({ key, value: JSON.stringify(value) }));
    });

    dispatch(
      setOption({
        key: "medicationPresentInfo",
        value:
          category_data.medicationPresentInfo ||
          "Medication is not present in the report",
      }),
    );

    dispatch(
      setOption({
        key: "medicationAbsentInfo",
        value:
          category_data.medicationAbsentInfo ||
          "Medication is present in the report",
      }),
    );

    dispatch(
      setOption({
        key: "medicationPresentTerminate",
        value: category_data.medicationPresentTerminate ?? true,
      }),
    );

    dispatch(
      setOption({
        key: "medicationAbsentTerminate",
        value: category_data.medicationAbsentTerminate ?? true,
      }),
    );
  }, [category_data, dispatch]);

  useEffect(() => {
    subtests.forEach((st) => {
      if (st.previous_type === 0) {
        if (
          st.days_or_tests !== null ||
          st.pdiv_below !== null ||
          st.pdiv_above !== null
        ) {
          dispatch(
            updateSubtest({
              id: st.id,
              changes: {
                days_or_tests: null,
                pdiv_below: null,
                pdiv_above: null,
              },
            }),
          );
        }
      } else if (st.previous_type === 2) {
        if (st.days_or_tests !== null) {
          dispatch(
            updateSubtest({
              id: st.id,
              changes: { days_or_tests: null },
            }),
          );
        }
      }
    });
  }, [subtests, dispatch]);

  /* --------------------------- Validation ----------------------------- */

  const validateForm = () => {
    let errors = [];

    const activeSubtests = subtests.filter((st) => st.deactivated !== true);
    const hasSubtestRules = activeSubtests.length > 0;

    const enabledKeywordRules = KEYWORD_RULE_DEFS.filter(
      ({ key }) => keywordRules[key]?.enabled,
    );

    const enabledMedicationRules = MEDICATION_RULE_DEFS.filter(
      ({ key }) => medicationRules[key]?.enabled,
    );

    const hasAnyRule =
      hasSubtestRules ||
      enabledKeywordRules.length > 0 ||
      enabledMedicationRules.length > 0;

    if (!hasAnyRule) {
      errors.push(
        "No rules have been applied. Please add at least one active subtest, keyword rule, or medication check before saving.",
      );
    }

    enabledKeywordRules.forEach(({ key, label }) => {
      if (!keywordRules[key]?.keywords?.trim()) {
        errors.push(
          `${label}: keywords must be specified while this rule is enabled.`,
        );
      }
    });

    enabledMedicationRules.forEach(({ key, label }) => {
      const rule = medicationRules[key];

      if (
        !rule?.medications?.trim() ||
        !rule?.issuedInMonths?.toString().trim()
      ) {
        errors.push(
          `${label}: medication and issued-in-months must both be provided while this rule is enabled.`,
        );
      }
    });

    if (options.followLab && subtests.length === 0) {
      errors.push(
        "At least one subtest is required when Follow Lab File Normal & Update Clinician Values is enabled.",
      );
    }

    for (const st of subtests) {
      if (st.deactivated === true) continue;

      const min =
        st.min_val !== null && st.min_val !== ""
          ? parseFloat(st.min_val)
          : null;
      const max =
        st.max_val !== null && st.max_val !== ""
          ? parseFloat(st.max_val)
          : null;

      if (min === null || max === null) {
        errors.push(
          `Min and Max value must be specified for ${st.sub_test_name}`,
        );
      }

      if (min !== null && max !== null) {
        if (!(min === 0 && max === 0) && min >= max) {
          errors.push(`Max must be greater than Min for ${st.sub_test_name}`);
        }
      }

      if (options.followLab) {
        const labMin =
          st.lab_min_val !== null && st.lab_min_val !== ""
            ? parseFloat(st.lab_min_val)
            : null;
        const labMax =
          st.lab_max_val !== null && st.lab_max_val !== ""
            ? parseFloat(st.lab_max_val)
            : null;

        if (labMin !== null && min !== null && min !== labMin) {
          errors.push(
            `Clinician Min Val must match Lab Min Val for ${st.sub_test_name}. Disable Automatically follow and file lab min max to use different values.`,
          );
        }

        if (labMax !== null && max !== null && max !== labMax) {
          errors.push(
            `Clinician Max Val must match Lab Max Val for ${st.sub_test_name}. Disable Automatically follow and file lab min max to use different values.`,
          );
        }
      }

      const minAge =
        st.min_age !== null && st.min_age !== "" ? parseInt(st.min_age) : null;
      const maxAge =
        st.max_age !== null && st.max_age !== "" ? parseInt(st.max_age) : null;

      if (minAge !== null && String(minAge) !== st.min_age) {
        errors.push(
          `Min Age must be a positive whole number for ${st.sub_test_name}`,
        );
      }

      if (maxAge !== null && String(maxAge) !== st.max_age) {
        errors.push(
          `Max Age must be a positive whole number for ${st.sub_test_name}`,
        );
      }

      if (maxAge === 0 || minAge === 0) {
        errors.push(
          `Min age and Max age should not be zero for ${st.sub_test_name}`,
        );
      }

      if (minAge !== null) {
        if (!Number.isInteger(minAge) || minAge < 0) {
          errors.push(
            `Min Age must be a positive whole number for ${st.sub_test_name}`,
          );
        }
      }

      if (maxAge !== null) {
        if (!Number.isInteger(maxAge) || maxAge < 0) {
          errors.push(
            `Max Age must be a positive whole number for ${st.sub_test_name}`,
          );
        }
      }

      if (
        (minAge !== null && maxAge === null) ||
        (minAge === null && maxAge !== null)
      ) {
        errors.push(
          `Both Min Age and Max Age must be specified for ${st.sub_test_name}`,
        );
      }

      if (minAge !== null && maxAge !== null) {
        if (minAge >= maxAge) {
          errors.push(
            `Max Age must be greater than Min Age for ${st.sub_test_name}`,
          );
        }
      }

      if (st.previous_type === 1) {
        if (!st.days_or_tests || (!st.pdiv_below && !st.pdiv_above)) {
          errors.push(
            `Previous test within X days missing supporting fields for ${st.sub_test_name}`,
          );
        }
      }

      if (st.previous_type === 2) {
        if (!st.pdiv_below && !st.pdiv_above) {
          errors.push(
            `Previous result missing supporting fields for ${st.sub_test_name}`,
          );
        }
      }
    }

    const seen = new Set();

    for (const st of subtests) {
      if (st.deactivated === true) continue;

      const key = getSubtestUniqueKey(st);

      if (seen.has(key)) {
        const gender = String(st.gender ?? "").trim() || "Any";

        const minAge =
          st.min_age === null ||
          st.min_age === undefined ||
          String(st.min_age).trim() === ""
            ? "Any"
            : String(st.min_age).trim();

        const maxAge =
          st.max_age === null ||
          st.max_age === undefined ||
          String(st.max_age).trim() === ""
            ? "Any"
            : String(st.max_age).trim();

        errors.push(
          `Duplicate test detected: Subtest - ${st.sub_test_name}, Gender - ${gender}, Age - ${minAge}-${maxAge}. Each test must have a unique subtest, gender and age combination.`,
        );
      } else {
        seen.add(key);
      }
    }

    const grouped = {};

    for (const st of subtests) {
      if (st.deactivated === true) continue;

      const key = st.sub_test_name?.trim().toLowerCase();

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(st);
    }

    Object.entries(grouped).forEach(([, tests]) => {
      const hasGeneric = tests.some((t) => {
        const gender = (t.gender ?? "").trim().toLowerCase();

        return (
          (gender === "" || gender === "both") &&
          (t.min_age === null || t.min_age === undefined || t.min_age === "") &&
          (t.max_age === null || t.max_age === undefined || t.max_age === "")
        );
      });

      const hasSpecific = tests.some((t) => {
        const gender = (t.gender ?? "").trim().toLowerCase();

        return (
          (gender !== "" && gender !== "both") ||
          (t.min_age !== null && t.min_age !== undefined && t.min_age !== "") ||
          (t.max_age !== null && t.max_age !== undefined && t.max_age !== "")
        );
      });

      if (hasGeneric && hasSpecific) {
        errors.push(
          `${tests[0].sub_test_name}: Generic and age/gender-specific rules cannot coexist. Delete the generic rule before creating age- or gender-based rules.`,
        );
      }
    });

    return errors;
  };

  const handleSave = () => {
    const errors = validateForm();

    if (errors.length > 0) {
      alert(errors.join("\n"));
      return;
    }

    dispatch(
      saveNormalRuleset({
        subtests,
        options,
        license_key,
        category: selectedCategory,
      }),
    );
  };

  const errors = validateForm();
  const isValid = errors.length === 0;

  const activeKeywordDef = KEYWORD_RULE_DEFS.find(
    (d) => d.key === keywordDialogKey,
  );
  const activeMedicationDef = MEDICATION_RULE_DEFS.find(
    (d) => d.key === medicationDialogKey,
  );

  return (
    <>
      {selectedCategory && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <BloodTestsTable
            category={selectedCategory}
            subTests={subtests}
            deploymentType={deploymentType}
            license_key={license_key}
          />
        </Box>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 1.25,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        {/* ---------------- Filing Options ---------------- */}

        {/* ---------------- Rule Configuration (compact) ---------------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 380,
            flex: 2,
          }}
        >
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            {KEYWORD_RULE_DEFS.map(({ key, label }) => {
              const rule = keywordRules[key] || EMPTY_KEYWORD_RULE;

              return (
                <RuleRow
                  key={key}
                  label={label}
                  enabled={rule.enabled}
                  summary={formatKeywordsHuman(rule.keywords)}
                  onToggle={(checked) => handleToggleKeywordRule(key, checked)}
                  onEdit={() => openKeywordDialog(key)}
                  onDelete={() => handleDeleteKeywordRule(key, label)}
                />
              );
            })}

            <Divider />

            {MEDICATION_RULE_DEFS.map(({ key, label }) => {
              const rule = medicationRules[key] || EMPTY_MEDICATION_RULE;

              return (
                <RuleRow
                  key={key}
                  label={label}
                  enabled={rule.enabled}
                  summary={formatMedicationHuman(rule)}
                  onToggle={(checked) =>
                    handleToggleMedicationRule(key, checked)
                  }
                  onEdit={() => openMedicationDialog(key)}
                  onDelete={() => handleDeleteMedicationRule(key, label)}
                />
              );
            })}
          </Box>
        </Box>

        {/* ---------------- Filing Approach ---------------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 160,
            flex: 1,
            p: "4px",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <FormGroup sx={{ m: 0 }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={options.skipWithoutRanges}
                  onChange={(e) => {
                    const newValue = e.target.checked;

                    const messageOn =
                      'When selecting - "Skip if no lab ranges are present" option, ' +
                      "MyBotGP will not file the test result if no subtest lines with ranges are found." +
                      "A minimum of one subtest line must be present to file.";

                    const messageOff =
                      'When de-selecting - "Skip if no lab ranges are present" option, ' +
                      "MyBotGP will file the test result even if no subtest lines with ranges are found." +
                      "No subtest lines need to be present to file.";

                    const result = window.confirm(
                      newValue ? messageOn : messageOff,
                    );

                    if (result) {
                      dispatch(
                        setOption({
                          key: "skipWithoutRanges",
                          value: newValue,
                        }),
                      );
                    } else {
                      e.preventDefault();
                      window.alert("The action has been cancelled.");
                    }
                  }}
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Skip if no lab ranges are present
                </span>
              }
            />

            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={options.followLab}
                  onChange={(e) => {
                    const newValue = e.target.checked;

                    if (newValue && (!subtests || subtests.length === 0)) {
                      window.alert(
                        "Please add at least one subtest before enabling Follow Lab File Normal & Update Clinician Values.",
                      );
                      return;
                    }

                    const messageOn =
                      'When selecting - Please note by selecting the "Follow Lab File Normal & Update Clinician Values" option, ' +
                      "MyBotGP will update existing subtest ranges with lab min and max values. The category will continue to be enabled.";

                    const messageOff =
                      'When de-selecting - Please note by de-selecting the "Follow Lab File Normal & Update Clinician Values" option, ' +
                      "MyBotGP will update existing subtest lab ranges and remove the category to In Review.";

                    const result = window.confirm(
                      newValue ? messageOn : messageOff,
                    );

                    if (newValue) {
                      const invalidSubtest = subtests.find((st) => {
                        const min =
                          st.min_val !== null && st.min_val !== ""
                            ? parseFloat(st.min_val)
                            : null;
                        const max =
                          st.max_val !== null && st.max_val !== ""
                            ? parseFloat(st.max_val)
                            : null;
                        const labMin =
                          st.lab_min_val !== null && st.lab_min_val !== ""
                            ? parseFloat(st.lab_min_val)
                            : null;
                        const labMax =
                          st.lab_max_val !== null && st.lab_max_val !== ""
                            ? parseFloat(st.lab_max_val)
                            : null;

                        return (
                          (labMin !== null && min !== null && min !== labMin) ||
                          (labMax !== null && max !== null && max !== labMax)
                        );
                      });

                      if (invalidSubtest) {
                        window.alert(
                          `Clinician ranges must match lab ranges before enabling Automatically follow and file lab min max.\n\nFailed test: ${invalidSubtest.sub_test_name}`,
                        );
                        return;
                      }
                    }

                    if (result) {
                      dispatch(
                        setOption({ key: "followLab", value: newValue }),
                      );
                    } else {
                      e.preventDefault();
                      window.alert("The action has been cancelled.");
                    }
                  }}
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Follow Lab File Normal & Update Clinician Values
                </span>
              }
            />

            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={options.sendAccurxNormal || false}
                  onChange={(e) =>
                    dispatch(
                      setOption({
                        key: "sendAccurxNormal",
                        value: e.target.checked,
                      }),
                    )
                  }
                />
              }
              label={
                <span style={{ fontSize: "0.8rem" }}>
                  Send Accurx message for normal report
                </span>
              }
            />

            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                disabled={!options.sendAccurxNormal}
                onClick={openAccurxDialog}
                sx={{ fontSize: "0.75rem", textTransform: "none" }}
              >
                {options.accurxNormalMessage ? "Edit Message" : "Set Message"}
              </Button>

              <Typography
                noWrap
                title={options.accurxNormalMessage || ""}
                sx={{
                  fontSize: "0.72rem",
                  color: options.sendAccurxNormal ? "text.secondary" : "#bbb",
                  fontStyle: options.accurxNormalMessage ? "normal" : "italic",
                  maxWidth: 180,
                }}
              >
                {options.accurxNormalMessage || "No message set"}
              </Typography>
            </Box>
          </FormGroup>

          <FormLabel sx={{ fontSize: "0.8rem", mb: 0.5 }}>
            Filing Approach
          </FormLabel>

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 1.2 }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={options.fileMethod === 1}
                  onChange={(e) =>
                    dispatch(
                      setOption({
                        key: "fileMethod",
                        value: e.target.checked ? 1 : 0,
                      }),
                    )
                  }
                />
              }
              label={<span style={{ fontSize: "0.8rem" }}>With Comments</span>}
              sx={{ mr: 0 }}
            />

            <Autocomplete
              freeSolo
              options={defaultComments}
              value={options.fileComment || ""}
              disabled={options.fileMethod === 0}
              onChange={(e, newValue) =>
                dispatch(setOption({ key: "fileComment", value: newValue }))
              }
              onInputChange={(e, newValue) =>
                dispatch(setOption({ key: "fileComment", value: newValue }))
              }
              sx={{ flex: 1, minWidth: 180 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select or type a comment"
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "4px 6px",
                      fontSize: "0.8rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      marginTop: "8px",
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 220,
            flex: 1,
          }}
        >
          {errors.length > 0 && (
            <Box sx={{ color: "red", fontSize: "0.8rem", mb: 1 }}>
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </Box>
          )}
          <Box sx={{ mt: 2, textAlign: "right" }}>
            {rulesuccess && (
              <Box sx={{ color: "green", fontSize: "0.8rem", mb: 1 }}>
                {rulesuccess}
              </Box>
            )}

            {ruleerror && (
              <Box sx={{ color: "red", fontSize: "0.8rem", mb: 1 }}>
                {ruleerror}
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={ruleloading || !isValid}
            >
              {ruleloading ? <CircularProgress size={20} /> : "Save RuleSet"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {selectedCategory && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Paper
            elevation={2}
            sx={{ pl: 2, pr: 2, pt: 1, maxHeight: 600, mt: 0 }}
          >
            <BloodTestsManager categoryId={selectedCategory.id} />
          </Paper>
        </Box>
      )}

      {/* ---------------- Keyword Check dialog ---------------- */}
      {activeKeywordDef && (
        <KeywordCheckDialog
          open={!!keywordDialogKey}
          title={`Configure: ${activeKeywordDef.label}`}
          checkType={activeKeywordDef.checkType}
          source={activeKeywordDef.source}
          hideAddRequiredGroup={activeKeywordDef.hideAddRequiredGroup}
          value={keywordDraft}
          onChange={setKeywordDraft}
          onSave={saveKeywordDialog}
          onCancel={cancelKeywordDialog}
          licenseKey={license_key}
          category={selectedCategory}
        />
      )}

      {/* ---------------- Medication Check dialog ---------------- */}
      {activeMedicationDef && (
        <MedicationCheckDialog
          open={!!medicationDialogKey}
          title={`Configure: ${activeMedicationDef.label}`}
          hideAddRequiredGroup={activeMedicationDef.hideAddRequiredGroup}
          value={medicationDraft}
          onChange={setMedicationDraft}
          onSave={saveMedicationDialog}
          onCancel={cancelMedicationDialog}
        />
      )}

      {/* ---------------- Accurx message dialog ---------------- */}
      <AccurxMessageDialog
        open={accurxDialogOpen}
        value={accurxDraft}
        onChange={setAccurxDraft}
        onSave={saveAccurxDialog}
        onCancel={cancelAccurxDialog}
      />
    </>
  );
};

export default NormalRuleFrame;
